import { useEffect, useRef } from "react";
import { X, AlertTriangle, AlertCircle, Info, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { useGuideUpdatesForAssistant } from "@/hooks/useGuideUpdatesRealtime";

const PRIORITY_META: Record<string, { icon: typeof Info; color: string; border: string; label: string }> = {
  urgent: { icon: AlertCircle, label: "Urgente", color: "text-red-500", border: "border-red-300 dark:border-red-800" },
  high: { icon: AlertTriangle, label: "Alta", color: "text-amber-500", border: "border-amber-300 dark:border-amber-800" },
  normal: { icon: Info, label: "Normal", color: "text-blue-500", border: "" },
};

const getPriority = (p: string) => PRIORITY_META[p] ?? PRIORITY_META.normal;

interface Props {
  assistantId: string | null;
  onClose: () => void;
  onMarkRead: (assistantId?: string) => Promise<void>;
}

export function GuideUpdatesPanel({ assistantId, onClose, onMarkRead }: Props) {
  const { updates, loading } = useGuideUpdatesForAssistant(assistantId);
  const markedRef = useRef(false);

  useEffect(() => {
    if (updates.length > 0 && !markedRef.current) {
      markedRef.current = true;
      void onMarkRead(assistantId ?? undefined);
    }
  }, [assistantId, updates.length, onMarkRead]);

  useEffect(() => {
    markedRef.current = false;
  }, [assistantId]);

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString("es", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="flex h-full flex-col border-l bg-background">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <h3 className="text-sm font-semibold">Novedades</h3>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading && (
          <p className="text-xs text-muted-foreground text-center py-8">Cargando…</p>
        )}

        {!loading && updates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCheck className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No hay novedades activas</p>
          </div>
        )}

        {updates.map((u) => {
          const p = getPriority(u.priority);
          const PIcon = p.icon;
          return (
            <div
              key={u.id}
              className={cn(
                "rounded-lg border p-3 space-y-1.5",
                p.border,
              )}
            >
              <div className="flex items-start gap-2">
                <PIcon className={cn("h-4 w-4 shrink-0 mt-0.5", p.color)} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{u.title}</div>
                  {u.description && (
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                      {u.description}
                    </p>
                  )}
                  {u.image_url && (
                    <ImageLightbox
                      src={u.image_url}
                      className="mt-2 rounded-md max-h-60 object-contain"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 pl-6">
                <Badge variant="secondary" className="text-[10px]">{p.label}</Badge>
                {(u.starts_at || u.ends_at) && (
                  <span className="text-[10px] text-muted-foreground">
                    {formatDate(u.starts_at)}
                    {u.starts_at && u.ends_at && " → "}
                    {formatDate(u.ends_at)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
